import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Token validation function
  const validateToken = async (token) => {
    try {
      // Simple token validation - check if token is not expired
      if (!token || token === '') {
        return false;
      }
      
      // Check if token follows expected format
      if (token.startsWith('tickleright_')) {
        // Extract timestamp from token
        const tokenParts = token.split('_');
        if (tokenParts.length >= 2) {
          const timestamp = parseInt(tokenParts[1]);
          const now = Date.now();
          // Check if token is older than 24 hours (86400000 ms)
          const tokenAge = now - timestamp;
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          
          if (tokenAge > maxAge) {
            console.log('Token expired');
            return false;
          }
          
          return true;
        }
      }
      
      // If it's not our custom token format, assume it's valid from API
      // You can add more validation here if needed
      return true;
      
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  };

  const checkAuthStatus = async () => {
     console.log('🔍 Starting auth check...');

    try {
      const token = localStorage.getItem('tickleright_token');
      const userData = localStorage.getItem('tickleright_user');

      console.log('📱 Found token:', !!token);
    console.log('👤 Found userData:', !!userData);
      
      if (token && userData) {
        const userDataParsed = JSON.parse(userData);
        
        // Validate the token
        const isValidToken = await validateToken(token);
              console.log('🔐 Token valid:', isValidToken);

        
        if (isValidToken) {
          // Token is valid, set authenticated state
          setUser(userDataParsed);
          setIsAuthenticated(true);
          console.log('✅ Token validated successfully');
        } else {
          // Token is invalid, clear auth data
          console.log('❌ Token validation failed, logging out');
          logout();
        }
      } else {
        // No token or user data found
        console.log('ℹ️ No stored credentials found');
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      logout();
    } finally {
      setLoading(false);
          console.log('🏁 Auth check complete, loading = false');

    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      
      // Replace with your actual API endpoint
      const response = await fetch(process.env.REACT_APP_ADMIN_LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
    auth: "1",
    password: credentials.password,
    username: credentials.phone,  // TickleRight uses 'username' for phone
    country_code: credentials.country_code || '+91',
}),      });

      const data = await response.json();

      console.log('📦 API Response Data:', data); // Debug log

      // Check if login was successful (error: 0 means success, error: 1 means failure)
      if (data.error == 0 && data.data) {
        const userData = data.data; // data is an object, not an array
        
        // Use the actual token from API or create one
        const token = userData.token || `tickleright_${Date.now()}_${userData.user_id}`;
        
        // Store token and user data
        localStorage.setItem('tickleright_token', token);
        localStorage.setItem('tickleright_user', JSON.stringify({
          id: userData.user_id,
          contact_id: userData.contact_id,
          name: userData.user_name || 'User',
          email: userData.email,
          phone: credentials.phone,
          role: userData.admin === "1" ? 'admin' : 'user',
          employee_id: userData.employee_id,
          home_page: userData.home_page
        }));
        
        setUser({
          id: userData.user_id,
          contact_id: userData.contact_id,
          name: userData.user_name || 'User',
          email: userData.email,
          phone: credentials.phone,
          role: userData.admin === "1" ? 'admin' : 'user',
          employee_id: userData.employee_id,
          home_page: userData.home_page
        });
        setIsAuthenticated(true);
        
        return { success: true, message: 'Login successful' };
      } else {
        // Check if there's a specific error message from API
        const errorMessage = data.error_msg || data.message || 'Invalid phone number or password';
        return { success: false, message: errorMessage };
      }
    //    else {
    //     return { success: false, message: 'Login failed' };
    //   }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('tickleright_token');
    localStorage.removeItem('tickleright_user');
    setUser(null);
    setIsAuthenticated(false);

  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

